import type { CSSProperties, ReactElement } from 'react';
import { Pause, Play, Square, X } from 'lucide-react';
import { formatMmSsFromMs } from '@/lib/format-time';
import { cn } from '@/lib/cn';

const DEFAULT_LEFT_OFFSET_PX = 220;

export type RecordingDockStatus = 'recording' | 'paused';

export type RecordingDockProps = {
  status: RecordingDockStatus;
  elapsedMs: number;
  waveformAmplitudes: number[];
  leftOffset?: number;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
};

export function RecordingDock({
  status,
  elapsedMs,
  waveformAmplitudes,
  leftOffset = DEFAULT_LEFT_OFFSET_PX,
  onStop,
  onPause,
  onResume,
  onCancel,
}: RecordingDockProps): ReactElement {
  const statusLabel = status === 'recording' ? 'RECORDING' : 'PAUSED';
  const pauseAction = status === 'recording' ? onPause : onResume;
  const pauseLabel = status === 'recording' ? 'Pause recording' : 'Resume recording';
  const PauseIcon = status === 'recording' ? Pause : Play;
  const style: CSSProperties = { ['--left-offset' as 'left']: `${leftOffset}px` };

  return (
    <section
      aria-label="Recording"
      style={style}
      className={cn(
        'fixed inset-x-0 bottom-0 z-[60] flex flex-wrap items-center justify-between gap-3 bg-[var(--ink)] text-[var(--bg-surface)]',
        'px-3 py-3 shadow-2xl border-t border-[var(--rule)]',
        'md:flex-nowrap md:gap-6 md:px-4 md:py-4 md:left-[var(--left-offset)] md:right-0',
      )}
    >
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest',
          status === 'recording'
            ? 'bg-[var(--err)] text-white'
            : 'bg-[var(--bg-soft)] text-[var(--ink-3)]',
        )}
      >
        {status === 'recording' && (
          <span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-white" />
        )}
        {statusLabel}
      </span>

      <time
        aria-label="Elapsed time"
        className="font-mono text-[40px] leading-none tabular-nums md:text-[64px]"
      >
        {formatMmSsFromMs(elapsedMs)}
      </time>

      <div
        aria-hidden="true"
        className="hidden h-12 flex-1 items-center gap-[2px] overflow-hidden md:flex"
      >
        {waveformAmplitudes.map((amplitude, index) => (
          <span
            key={index}
            className="w-1 rounded-full bg-[var(--bg-surface)] opacity-80"
            style={{ height: `${Math.max(8, Math.round(amplitude * 48))}px` }}
          />
        ))}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Cancel recording"
          onClick={onCancel}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={pauseLabel}
          onClick={pauseAction}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--rule)]"
        >
          <PauseIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Stop and review"
          onClick={onStop}
          className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--err)] text-white shadow-lg hover:opacity-90"
        >
          <Square className="h-6 w-6 fill-current" />
        </button>
      </div>
    </section>
  );
}
